import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../api/axios';
import { getSiteAdminTemplatesRoute, getSiteAdminTemplateCategoriesRoute } from '../../api/routes';
import { toast } from 'react-toastify';
import './SiteAdmin.scss';
import BannerEditor from './tabs/BannerEditor';
import TemplateManager from './tabs/TemplateManager';
import EditTemplate from './tabs/EditTemplate';
import TemplateCategoryManager from './tabs/TemplateCategoryManager';
import EditTemplateCategory from './tabs/EditTemplateCategory';

const TABS = [
    { id: 'page-banners', label: 'Page Banners' },
    { id: 'templates', label: 'Templates' },
    { id: 'template-categories', label: 'Template Categories' },
];

// Derive view state from URL params produced by the /site-admin/* wildcard route.
// Possible URL patterns:
//   /site-admin                               → tab: page-banners
//   /site-admin/page-banners                  → tab: page-banners
//   /site-admin/templates                     → tab: templates
//   /site-admin/templates/:templateId         → tab: templates, editing template
//   /site-admin/template-categories           → tab: template-categories
//   /site-admin/template-categories/:catId    → tab: template-categories, editing category
const parsePath = (splat) => {
    if (!splat) return { tab: 'page-banners', templateId: null, categoryId: null };
    const parts = splat.replace(/^\//, '').split('/');
    const tab = parts[0] || 'page-banners';
    const templateId = parts[0] === 'templates' && parts[1] ? parts[1] : null;
    const categoryId = parts[0] === 'template-categories' && parts[1] ? parts[1] : null;
    return { tab, templateId, categoryId };
};

const SiteAdmin = () => {
    const navigate = useNavigate();
    // React Router v6 wildcard gives us the remainder after /site-admin/ as '*'
    const params = useParams();
    const splat = params['*'] || '';
    const { tab: urlTab, templateId: urlTemplateId, categoryId: urlCategoryId } = parsePath(splat);

    const [templates, setTemplates] = useState(null); // cached list so EditTemplate can be resolved
    const [templatePatch, setTemplatePatch] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const [categories, setCategories] = useState(null); // cached list so EditTemplateCategory can be resolved
    const [categoryPatch, setCategoryPatch] = useState(null);
    const [loadingCategories, setLoadingCategories] = useState(false);

    // When arriving directly at a template edit URL (e.g. browser back from campaign),
    // TemplateManager never mounts so we must fetch the list ourselves.
    useEffect(() => {
        if (urlTemplateId && templates === null && !loadingTemplates) {
            setLoadingTemplates(true);
            axios.get(getSiteAdminTemplatesRoute, { withCredentials: true })
                .then(res => {
                    if (res.data.success) setTemplates(res.data.templates);
                })
                .catch(() => toast.error('Failed to load templates'))
                .finally(() => setLoadingTemplates(false));
        }
    }, [urlTemplateId, templates, loadingTemplates]); // eslint-disable-line react-hooks/exhaustive-deps

    // Same for category edit URL
    useEffect(() => {
        if (urlCategoryId && urlCategoryId !== 'new' && categories === null && !loadingCategories) {
            setLoadingCategories(true);
            axios.get(getSiteAdminTemplateCategoriesRoute, { withCredentials: true })
                .then(res => {
                    if (res.data.success) setCategories(res.data.categories);
                })
                .catch(() => toast.error('Failed to load categories'))
                .finally(() => setLoadingCategories(false));
        }
    }, [urlCategoryId, categories, loadingCategories]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve the editing template object from the cached list when the URL has a templateId
    const isNewTemplate = urlTemplateId === 'new';
    const editingTemplate = !isNewTemplate && urlTemplateId && templates
        ? (templates.find(t => t.id === urlTemplateId) || null)
        : null;

    // Resolve the editing category object
    const isNewCategory = urlCategoryId === 'new';
    const editingCategory = !isNewCategory && urlCategoryId && categories
        ? (categories.find(c => c.id === urlCategoryId) || null)
        : null;

    const navigateToTab = (tabId) => {
        navigate(`/site-admin/${tabId}`);
    };

    const openEditTemplate = (template) => {
        navigate(`/site-admin/templates/${template.id}`);
    };

    const openCreateTemplate = () => {
        navigate('/site-admin/templates/new');
    };

    const handleBack = () => {
        navigate('/site-admin/templates');
    };

    const handleEditSaved = (updated, { stayOnPage = false } = {}) => {
        if (updated && updated.id) {
            setTemplatePatch(updated);
            setTemplates(prev => {
                if (!prev) return [updated];
                return prev.some(t => t.id === updated.id)
                    ? prev.map(t => t.id === updated.id ? { ...t, ...updated } : t)
                    : [...prev, updated];
            });
            // New template: go to its edit URL
            if (isNewTemplate) {
                navigate(`/site-admin/templates/${updated.id}`);
                return;
            }
            // In-place update (e.g. campaign created): stay on page
            if (stayOnPage) return;
        }
        navigate('/site-admin/templates');
    };

    // Keep the cached templates list up to date when TemplateManager loads/patches them
    const handleTemplatesLoaded = (list) => setTemplates(list);
    const handleFlagChange = (id, patch) => {
        setTemplates(prev => prev ? prev.map(t => t.id === id ? { ...t, ...patch } : t) : prev);
    };

    // When a templatePatch arrives after a save, merge it into the cache
    useEffect(() => {
        if (templatePatch) {
            setTemplates(prev => prev ? prev.map(t => t.id === templatePatch.id ? { ...t, ...templatePatch } : t) : prev);
        }
    }, [templatePatch]);

    // Category navigation helpers
    const openEditCategory = (category) => {
        navigate(`/site-admin/template-categories/${category.id}`);
    };

    const openCreateCategory = () => {
        navigate('/site-admin/template-categories/new');
    };

    const handleCategoryBack = () => {
        navigate('/site-admin/template-categories');
    };

    const handleCategorySaved = (updated) => {
        if (updated && updated.id) {
            setCategoryPatch(updated);
            setCategories(prev => {
                if (!prev) return [updated];
                return prev.some(c => c.id === updated.id)
                    ? prev.map(c => c.id === updated.id ? { ...c, ...updated } : c)
                    : [...prev, updated];
            });
            if (isNewCategory) {
                navigate(`/site-admin/template-categories/${updated.id}`);
                return;
            }
        }
        navigate('/site-admin/template-categories');
    };

    useEffect(() => {
        if (categoryPatch) {
            setCategories(prev => prev ? prev.map(c => c.id === categoryPatch.id ? { ...c, ...categoryPatch } : c) : prev);
        }
    }, [categoryPatch]);

    // Redirect bare /site-admin to /site-admin/page-banners
    useEffect(() => {
        if (!splat) {
            navigate('/site-admin/page-banners', { replace: true });
        }
    }, [splat]); // eslint-disable-line react-hooks/exhaustive-deps

    if (urlTemplateId) {
        return (
            <div className="content-page-container site-admin-page">
                <div className="site-admin-content">
                    {isNewTemplate ? (
                        <EditTemplate
                            template={{}}
                            isNew={true}
                            onBack={handleBack}
                            onSaved={handleEditSaved}
                        />
                    ) : editingTemplate ? (
                        <EditTemplate
                            template={editingTemplate}
                            onBack={handleBack}
                            onSaved={handleEditSaved}
                        />
                    ) : (
                        <div className="home-loading-placeholder" />
                    )}
                </div>
            </div>
        );
    }

    if (urlCategoryId) {
        return (
            <div className="content-page-container site-admin-page">
                <div className="site-admin-content">
                    {isNewCategory ? (
                        <EditTemplateCategory
                            category={{}}
                            isNew={true}
                            onBack={handleCategoryBack}
                            onSaved={handleCategorySaved}
                        />
                    ) : editingCategory ? (
                        <EditTemplateCategory
                            category={editingCategory}
                            onBack={handleCategoryBack}
                            onSaved={handleCategorySaved}
                        />
                    ) : (
                        <div className="home-loading-placeholder" />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="content-page-container site-admin-page">
            <div className="content-page-top">
                <div className="fl-column grow">
                    <div className="content-top-row">
                        <div className="fl-row-slim">
                            <h4 className="page-title">Site Admin</h4>
                        </div>
                        <div className="spacing-line" />
                    </div>
                    <div className="site-admin-tabs">
                        {TABS.map(tab => (
                            <div
                                key={tab.id}
                                className={`site-admin-tab${urlTab === tab.id ? ' active' : ''}`}
                                onClick={() => navigateToTab(tab.id)}
                            >
                                {tab.label}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="site-admin-content">
                {urlTab === 'page-banners' && <BannerEditor />}
                {urlTab === 'templates' && (
                    <TemplateManager
                        onEdit={openEditTemplate}
                        onCreate={openCreateTemplate}
                        templatePatch={templatePatch}
                        onTemplatesLoaded={handleTemplatesLoaded}
                        onFlagChange={handleFlagChange}
                    />
                )}
                {urlTab === 'template-categories' && (
                    <TemplateCategoryManager
                        onEdit={openEditCategory}
                        onCreate={openCreateCategory}
                        categoryPatch={categoryPatch}
                    />
                )}
            </div>
        </div>
    );
};

export default SiteAdmin;
